import React, { Fragment, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Typography,
  Input,
  SelectGroup,
  SelectGroupOption,
  SvgIcon,
  Button,
  clamp,
  useDidUpdate,
  useDidMount,
} from "@gemlightbox/core-kit";

import { hexToRgba, Mat3, Vec2, Webgl2New } from "src/image-editor-core";

import { useStores } from "src/hooks";
import { getStaticUrl, unsavedChangesNotification } from "src/utils";
import { editMediaStore } from "../../edit-media.store";
import { GroupBlock } from "../group-block";
import { marketplacesArr } from "./resize.constants";

import styles from "./resize.module.css";

export const Resize: React.FC = observer(() => {
  const { renderer } = editMediaStore;
  const canvasSize = renderer.canvasSize;

  const { localeStore } = useStores();

  const [prevCanvasTranslation] = useState(() => renderer.canvasTranslation.clone());
  const [prevCanvasSize] = useState(() => renderer.canvasSize.clone());
  const [selectedSizeOption, setSelectedSizeOption] = useState("Custom");
  const [paddingTextureComponent] = useState(() => new Webgl2New.TextureComponent());

  const setCanvasSizeCentered = (width: number, height: number) => {
    const targetCenter = (() => {
      const main = editMediaStore.mainImageComponent;
      if (!main) return renderer.canvasGlobalCenter;
      try {
        return Webgl2New.Webgl2dRenderer.global.getComponentClipSpaceInGlobal(main, Vec2.half());
      } catch {
        return renderer.canvasGlobalCenter;
      }
    })();
    renderer.setCanvasSize(width, height, false);
    const nextTranslation = targetCenter.clone().sub(renderer.canvasSize.clone().divide(2)).round();
    renderer.setCanvasTranslate(nextTranslation, true);
  };

  const handleChangeWidth = (width: string) => {
    let widthToSet = parseInt(width);
    if (isNaN(widthToSet)) widthToSet = canvasSize.width;
    widthToSet = clamp(widthToSet, Webgl2New.minCanvasSize, Webgl2New.maxCanvasSize);

    setSelectedSizeOption("Custom");
    setCanvasSizeCentered(widthToSet, renderer.canvasSize.height);
  };

  const handleChangeHeight = (height: string) => {
    let heightToSet = parseInt(height);
    if (isNaN(heightToSet)) heightToSet = renderer.canvasSize.height;
    heightToSet = clamp(heightToSet, Webgl2New.minCanvasSize, Webgl2New.maxCanvasSize);

    setSelectedSizeOption("Custom");
    setCanvasSizeCentered(canvasSize.width, heightToSet);
  };

  const handleChangeMarketplace = (value: string) => {
    const foundItem = marketplacesArr.find((item) => item.value === value);
    setSelectedSizeOption(foundItem?.label ?? "Custom");
    if (!foundItem) return;
    const [width, height] = foundItem.size.split(" x ");
    setCanvasSizeCentered(Number(width), Number(height));
  };

  const checkNeedWarning = () => {
    return !(
      prevCanvasSize.equals(renderer.canvasSize) &&
      prevCanvasTranslation.equals(renderer.canvasTranslation)
    );
  };

  const handleCancel = () => {
    editMediaStore.unregisterTabBlockCallback();
    renderer.setCanvasTranslate(prevCanvasTranslation.width, prevCanvasTranslation.height);
    renderer.setCanvasSize(prevCanvasSize.width, prevCanvasSize.height, true);
    editMediaStore.closeResizeTab();
  };

  const handleConfirmCancel = () => {
    if (checkNeedWarning()) {
      unsavedChangesNotification(handleCancel);
      return;
    }

    handleCancel();
  };

  const handleDone = () => {
    editMediaStore.unregisterTabBlockCallback();

    const mainComponent = editMediaStore.mainImageComponent;
    if (!mainComponent) {
      editMediaStore.closeResizeTab();
      return;
    }

    const undoTranslation = prevCanvasTranslation.clone();
    const undoSize = prevCanvasSize.clone();
    const undoTexture = mainComponent.state.texture.clone();
    const undoTransform = mainComponent.transform.clone();

    const actionTranslation = renderer.canvasTranslation.clone();
    let actionSize = renderer.canvasSize.clone();

    editMediaStore.closeResizeTab();

    if (undoTranslation.equals(actionTranslation) && undoSize.equals(actionSize)) return;

    const selectedTopLeft = Webgl2New.Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      mainComponent,
      Vec2.zero(),
    );
    const canvasTopLeft = actionTranslation.clone();
    const canvasBottomRight = actionTranslation.clone().add(actionSize);
    const tm3 = Mat3.identity()
      .rotate(-mainComponent.transform.rads)
      .translate(selectedTopLeft.clone().negate());

    const canvasTopLeftLocal = canvasTopLeft.transformMat3(tm3.mat);
    const canvasBottomRightLocal = canvasBottomRight.transformMat3(tm3.mat);

    const rawMinX = Math.min(canvasTopLeftLocal.x, canvasBottomRightLocal.x);
    const rawMaxX = Math.max(canvasTopLeftLocal.x, canvasBottomRightLocal.x);
    const rawMinY = Math.min(canvasTopLeftLocal.y, canvasBottomRightLocal.y);
    const rawMaxY = Math.max(canvasTopLeftLocal.y, canvasBottomRightLocal.y);

    const minX = Math.max(0, rawMinX);
    const maxX = Math.min(mainComponent.transform.size.width, rawMaxX);
    const minY = Math.max(0, rawMinY);
    const maxY = Math.min(mainComponent.transform.size.height, rawMaxY);

    const isCanvasInsideComponent =
      rawMinX >= 0 &&
      rawMinY >= 0 &&
      rawMaxX <= mainComponent.transform.size.width &&
      rawMaxY <= mainComponent.transform.size.height;
    const canCropTexture =
      isCanvasInsideComponent &&
      maxX > minX &&
      maxY > minY &&
      (minX > 0 ||
        minY > 0 ||
        maxX < mainComponent.transform.size.width ||
        maxY < mainComponent.transform.size.height);

    const actionTransform = mainComponent.transform.clone();
    let actionTexture = mainComponent.state.texture.clone();
    let shouldUpdateTexture = false;

    if (canCropTexture) {
      const ratio = mainComponent.state.texture.textureSize
        .clone()
        .divide(mainComponent.transform.size);
      const cropSize = new Vec2(maxX - minX, maxY - minY).multiply(ratio);
      const cropOffset = new Vec2(minX, minY).multiply(ratio);

      Webgl2New.Texture.cropTexture(
        mainComponent.state.texture,
        actionTexture,
        cropOffset.x,
        cropOffset.y,
        cropSize.width,
        cropSize.height,
      );
      actionSize = actionTexture.textureSize.clone();
      actionTransform.calcDimensions({
        position: renderer.canvasGlobalCenter,
        size: actionSize,
        rotation: mainComponent.transform.degrees,
        scale: Vec2.one(),
      });
      shouldUpdateTexture = true;
    } else if (!isCanvasInsideComponent) {
      const paddedTexture = new Webgl2New.Texture(actionSize);
      const drawTranslation = selectedTopLeft.clone().sub(canvasTopLeft).round();
      paddingTextureComponent.transform.setSize(mainComponent.transform.size);
      paddingTextureComponent.transform.setTranslation(drawTranslation);

      paddedTexture.drawIntoTexture(() => {
        const gl = Webgl2New.Webgl2.gl;
        const [r, g, b] = hexToRgba(editMediaStore.currentBGColor, true);
        gl.clearStencil(0);
        gl.clearColor(r, g, b, 0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
        Webgl2New.Webgl2.renderComponent(paddingTextureComponent, {
          texture: mainComponent.state.texture,
        });
      });

      actionTexture = paddedTexture;
      actionSize = actionTexture.textureSize.clone();
      actionTransform.calcDimensions({
        position: renderer.canvasGlobalCenter,
        size: actionSize,
        rotation: mainComponent.transform.degrees,
        scale: Vec2.one(),
      });
      shouldUpdateTexture = true;
    }

    renderer.commands.execute(
      "Resize canvas",
      () => {
        renderer.setCanvasTranslate(actionTranslation.width, actionTranslation.height);
        renderer.setCanvasSize(actionSize, true);
        renderer.selectComponent(mainComponent);
        if (shouldUpdateTexture) {
          mainComponent.transform.copy(actionTransform);
          mainComponent.state.texture = actionTexture.clone();
        }
        renderer.selectionComponent.copyComponentTransform(mainComponent);
      },
      () => {
        renderer.setCanvasTranslate(undoTranslation.width, undoTranslation.height);
        renderer.setCanvasSize(undoSize, true);
        renderer.selectComponent(mainComponent);
        if (shouldUpdateTexture) {
          mainComponent.transform.copy(undoTransform);
          mainComponent.state.texture = undoTexture.clone();
        }
        renderer.selectionComponent.copyComponentTransform(mainComponent);
      },
    );
  };

  useDidMount(() => {
    return editMediaStore.registerTabBlockCallback(({ unregister, proceed }) => {
      if (checkNeedWarning()) {
        unsavedChangesNotification(() => {
          unregister();
          handleCancel();
          proceed();
        });

        return true;
      }

      unregister();
      handleCancel();
    });
  });

  useDidUpdate(() => {
    // NOTE: If we change width\height via dropdown we should not set value to "Custom"
    // So this condition is workaround
    const foundItem = marketplacesArr.find((item) => item.label === selectedSizeOption);
    if (foundItem) {
      const [width, height] = foundItem.size.split(" x ");
      if (Number(width) === canvasSize.width && Number(height) === canvasSize.height) {
        return;
      }
    }

    // NOTE: If we change width\height via resizing controls we change option to be custom
    setSelectedSizeOption("Custom");
  }, [selectedSizeOption, canvasSize.width, canvasSize.height]);

  return (
    <Fragment>
      <GroupBlock className={styles.customResizeBlockGroup}>
        <Typography size="small600" color="textSecondary">
          {localeStore.t('["edit-media"].inspector.resize.custom')}
        </Typography>

        <div className={styles.inputsWrapper}>
          <Input
            type="number"
            inputWrapperClassName={styles.inputWrapper}
            label={localeStore.t("common.helpers.width")}
            value={canvasSize.width}
            appearance="primaryV2"
            suffix="px"
            onChange={handleChangeWidth}
            disableError
            forceAffix
          />
          <Input
            type="number"
            inputWrapperClassName={styles.inputWrapper}
            label={localeStore.t("common.helpers.height")}
            value={canvasSize.height}
            appearance="primaryV2"
            suffix="px"
            onChange={handleChangeHeight}
            disableError
            forceAffix
          />
        </div>
      </GroupBlock>

      <GroupBlock className={styles.marketplacesBlockGroup}>
        <Typography size="small600" color="textSecondary">
          {localeStore.t('["edit-media"].inspector.resize.marketplaces')}
        </Typography>

        <SelectGroup
          searchValue={selectedSizeOption}
          value={selectedSizeOption}
          onChange={handleChangeMarketplace}
          disableSearch
          disableClear
          disableError
        >
          {marketplacesArr.map(({ label, value, size, staticItem }) => (
            <SelectGroupOption
              key={label}
              contentContainerClassName={styles.selectOptionContent}
              value={value}
            >
              <SvgIcon icon={getStaticUrl(staticItem)} disableAutoColor />
              <Typography size="extraSmall" color="textSecondary">
                {label}
              </Typography>
              <Typography size="extraSmall" color="textTertiary">
                {size}
              </Typography>
            </SelectGroupOption>
          ))}
        </SelectGroup>
      </GroupBlock>

      <div className={styles.buttons}>
        <Button appearance="secondaryOutlined" onClick={handleConfirmCancel}>
          {localeStore.t("common.buttons.cancel")}
        </Button>
        <Button onClick={handleDone}>{localeStore.t("common.buttons.done")}</Button>
      </div>
    </Fragment>
  );
});

export default Resize;
